import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [subscriptions, setSubscriptions] = useState(new Set());
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const maxReconnectAttempts = 5;

  const connect = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;
    
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      setReconnectAttempts(0);
      
      // Authenticate if user is logged in
      if (isAuthenticated && token) {
        wsRef.current.send(JSON.stringify({
          type: 'auth',
          token
        }));
      }
    };

    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    wsRef.current.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
      
      // Attempt to reconnect
      if (reconnectAttempts < maxReconnectAttempts) {
        const delay = Math.pow(2, reconnectAttempts) * 1000; // Exponential backoff
        reconnectTimeoutRef.current = setTimeout(() => {
          setReconnectAttempts(prev => prev + 1);
          connect();
        }, delay);
      }
    };

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  };

  const disconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setSubscriptions(new Set());
  };

  const handleMessage = (data) => {
    switch (data.type) {
      case 'connected':
        console.log('WebSocket connection established');
        break;
        
      case 'auth_success':
        console.log('WebSocket authentication successful');
        // Re-subscribe to previous channels
        if (subscriptions.size > 0) {
          subscribe(Array.from(subscriptions));
        }
        break;
        
      case 'auth_error':
        console.error('WebSocket authentication failed:', data.message);
        break;
        
      case 'broadcast':
        handleBroadcast(data);
        break;
        
      case 'subscribed':
        console.log('Subscribed to channels:', data.channels);
        break;
        
      case 'error':
        console.error('WebSocket error:', data.message);
        break;
        
      default:
        console.log('Unknown message type:', data.type);
    }
  };

  const handleBroadcast = (data) => {
    const { channel, data: broadcastData } = data;
    
    switch (channel) {
      case 'recommendations':
        // Handle recommendation updates
        window.dispatchEvent(new CustomEvent('recommendationUpdate', {
          detail: broadcastData
        }));
        break;
        
      case 'news':
        // Handle news updates
        window.dispatchEvent(new CustomEvent('newsUpdate', {
          detail: broadcastData
        }));
        toast.success('New market news available');
        break;
        
      default:
        if (channel.startsWith('price:')) {
          // Handle price updates
          const ticker = channel.split(':')[1];
          window.dispatchEvent(new CustomEvent('priceUpdate', {
            detail: { ticker, ...broadcastData }
          }));
        }
    }
  };

  const subscribe = (channels) => {
    if (!isConnected || !wsRef.current) {
      console.warn('WebSocket not connected, cannot subscribe');
      return;
    }

    const channelsArray = Array.isArray(channels) ? channels : [channels];
    
    wsRef.current.send(JSON.stringify({
      type: 'subscribe',
      channels: channelsArray
    }));

    // Update local subscriptions
    setSubscriptions(prev => {
      const newSubs = new Set(prev);
      channelsArray.forEach(channel => newSubs.add(channel));
      return newSubs;
    });
  };

  const unsubscribe = (channels) => {
    if (!isConnected || !wsRef.current) {
      return;
    }

    const channelsArray = Array.isArray(channels) ? channels : [channels];
    
    wsRef.current.send(JSON.stringify({
      type: 'unsubscribe',
      channels: channelsArray
    }));

    // Update local subscriptions
    setSubscriptions(prev => {
      const newSubs = new Set(prev);
      channelsArray.forEach(channel => newSubs.delete(channel));
      return newSubs;
    });
  };

  const subscribeToStock = (ticker) => {
    subscribe(`price:${ticker.toUpperCase()}`);
  };

  const unsubscribeFromStock = (ticker) => {
    unsubscribe(`price:${ticker.toUpperCase()}`);
  };

  const subscribeToRecommendations = () => {
    subscribe('recommendations');
  };

  const subscribeToNews = () => {
    subscribe('news');
  };

  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, []);

  // Reconnect when authentication status changes
  useEffect(() => {
    if (isConnected && isAuthenticated && token) {
      wsRef.current?.send(JSON.stringify({
        type: 'auth',
        token
      }));
    }
  }, [isAuthenticated, token, isConnected]);

  const value = {
    isConnected,
    subscribe,
    unsubscribe,
    subscribeToStock,
    unsubscribeFromStock,
    subscribeToRecommendations,
    subscribeToNews,
    reconnectAttempts,
    maxReconnectAttempts
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};